import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import Home from '@/pages/Home';
// Arquivo direto, não o barril: este módulo é o ponto de entrada e importar de
// `@/components/ui` traria os 20 componentes e todo o Radix para o boot por
// causa de um indicador de carga.
import { Spinner } from '@/components/ui/Spinner';

/**
 * Rotas da aplicação.
 *
 * POR QUE `createBrowserRouter` E NÃO `<BrowserRouter>`:
 * `ScrollRestoration`, usada no Layout, **só funciona em data router**. Sob o
 * roteador declarativo ela falha em tempo de execução. Sem ela, ir de `/exames`
 * para a ficha de um exame mantém a rolagem no meio da listagem anterior — o
 * visitante cai no meio de um texto e acha que a página carregou errado.
 *
 * DIVISÃO DE CÓDIGO POR ROTA (F4.8.1). Só a Home entra no pacote inicial, por
 * ser onde a maioria das visitas começa. O resto é carregado sob demanda, e
 * isso importa em três lugares concretos:
 *   · o painel administrativo nunca é baixado por quem só compra;
 *   · o checkout, com formulário, validação e máscaras, não pesa na primeira
 *     visita de quem está só pesquisando preço;
 *   · o catálogo de 54 fichas não entra no caminho de quem veio pelo telefone
 *     de uma unidade.
 * É o que leva o pacote inicial de 449 KB para o alvo de menos de 180 KB.
 */

/* ------------------------------------------------------------ Institucional */
const Sobre = lazy(() => import('@/pages/Sobre'));
const Unidades = lazy(() => import('@/pages/Unidades'));
const ViverMais = lazy(() => import('@/pages/ViverMais'));
const Contato = lazy(() => import('@/pages/Contato'));
const Legal = lazy(() => import('@/pages/Legal'));
const NotFound = lazy(() => import('@/pages/NotFound'));

/* ------------------------------------------------------------------ Catálogo */
const Exames = lazy(() => import('@/pages/Exames'));
const ExameDetalhe = lazy(() => import('@/pages/ExameDetalhe'));

/* ------------------------------------------------------------------- Compra */
const Carrinho = lazy(() => import('@/pages/Carrinho'));
const Checkout = lazy(() => import('@/pages/Checkout'));
const PedidoStatus = lazy(() => import('@/pages/PedidoStatus'));
const Voucher = lazy(() => import('@/pages/Voucher'));

/* ------------------------------------------------------------------ Painel */
const AdminLayout = lazy(() => import('@/pages/admin/AdminLayout'));
const AdminPedidos = lazy(() => import('@/pages/admin/Pedidos'));
const AdminPedidoDetalhe = lazy(() => import('@/pages/admin/PedidoDetalhe'));
const AdminPrecos = lazy(() => import('@/pages/admin/Precos'));
const AdminUnidades = lazy(() => import('@/pages/admin/Unidades'));
const AdminSaude = lazy(() => import('@/pages/admin/Saude'));

/** Mostrado enquanto o pedaço da rota é baixado. */
function Carregando() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" role="status" aria-live="polite">
      <Spinner className="w-8 h-8 text-santa-green" />
      <span className="sr-only">Carregando página</span>
    </div>
  );
}

const rotas = createRoutesFromElements(
  <>
    {/*
      O comprovante fica FORA do Layout, de propósito: precisa nascer sem
      cabeçalho de navegação e sem rodapé para caber numa folha A4 limpa. As
      classes `no-print` da própria página são a segunda defesa, caso alguém o
      mova para dentro do Layout sem perceber o efeito na impressão.

      O caminho fica sob o prefixo `/pedido/` também por privacidade: é esse
      prefixo que o `robots.txt` bloqueia. Uma rota `/voucher/:token` ficaria de
      fora dessa regra, e sobrariam só o `noindex` do HTML e o cabeçalho da
      função de servidor — numa página que nomeia paciente e exame.
    */}
    <Route path="/pedido/:token/voucher" element={<Voucher />} />

    {/*
      Painel administrativo com casca própria: navegação lateral distinta do
      site público, para ninguém confundir os contextos, e guarda de sessão
      dentro do próprio AdminLayout.

      Esconder o menu NÃO é a segurança. A autorização real está nas políticas
      de acesso por linha do banco e nas funções de servidor; isto aqui é
      experiência de uso.
    */}
    <Route path="/admin" element={<AdminLayout />}>
      <Route index element={<AdminPedidos />} />
      <Route path="pedidos" element={<AdminPedidos />} />
      <Route path="pedidos/:id" element={<AdminPedidoDetalhe />} />
      <Route path="precos" element={<AdminPrecos />} />
      <Route path="unidades" element={<AdminUnidades />} />
      {/*
        Painel de saúde da operação: pedidos pagos sem ordem de serviço há mais
        de 4 horas, pagamentos parados, e divergências de conciliação abertas.
        É o que transforma "aviso de pagamento perdido" de incidente invisível
        em item de checklist da manhã.
      */}
      <Route path="saude" element={<AdminSaude />} />
    </Route>

    {/* Site público */}
    <Route path="/" element={<Layout />}>
      <Route index element={<Home />} />
      <Route path="sobre" element={<Sobre />} />

      <Route path="exames" element={<Exames />} />
      {/*
        54 páginas de aquisição orgânica que antes não existiam. É onde está a
        busca de alta intenção — "exame de vitamina D preço Três Rios" — e
        nenhum laboratório da microrregião tem catálogo indexado hoje.
      */}
      <Route path="exames/:slug" element={<ExameDetalhe />} />

      <Route path="unidades" element={<Unidades />} />
      <Route path="viver-mais" element={<ViverMais />} />
      <Route path="contato" element={<Contato />} />

      {/* Fluxo de compra */}
      <Route path="carrinho" element={<Carrinho />} />
      <Route path="checkout" element={<Checkout />} />
      <Route path="pedido/:token" element={<PedidoStatus />} />

      {/*
        Documentos legais: três caminhos estáticos para a mesma página, que
        decide qual documento mostrar pelo caminho. Uma rota dinâmica de um
        segmento capturaria qualquer coisa e roubaria o 404.
      */}
      <Route path="termos" element={<Legal />} />
      <Route path="privacidade" element={<Legal />} />
      <Route path="cancelamento" element={<Legal />} />

      <Route path="*" element={<NotFound />} />
    </Route>
  </>,
);

const router = createBrowserRouter(rotas);

function App() {
  return (
    <Suspense fallback={<Carregando />}>
      <RouterProvider router={router} />
    </Suspense>
  );
}

export default App;
